/* ==========================================================================
   Cart Drawer — oddit-cart-drawer
   Interactive behaviors: open/close, overlay click, escape key, focus trap
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: '.oddit-cart-drawer',
    overlay: '.oddit-cart-drawer__overlay',
    panel: '.oddit-cart-drawer__panel',
    closeBtn: '.oddit-cart-drawer__close',
  };

  const CLASSES = {
    open: 'is-open',
  };

  function initCartDrawer(container) {
    const drawer = container || document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    const overlay = drawer.querySelector(SELECTORS.overlay);
    const panel = drawer.querySelector(SELECTORS.panel);

    if (!overlay || !panel) return;

    let previouslyFocusedElement = null;

    // Cache the empty-state collection cards from the initial (correct) render.
    // refreshDrawer() re-renders the drawer via the Section Rendering API, and a
    // section that lives in a section group can come back WITHOUT its block
    // config — which drops these cards. Restoring from this cache keeps the
    // empty-cart category cards showing every time, not intermittently.
    var cachedEmptyCollections = null;
    var initialCollections = panel.querySelector('.oddit-cart-drawer__collections');
    if (initialCollections) cachedEmptyCollections = initialCollections.outerHTML;

    function restoreEmptyStateCards() {
      var emptyState = panel.querySelector('.oddit-cart-drawer__empty-state');
      if (!emptyState || !cachedEmptyCollections) return;
      var collections = emptyState.querySelector('.oddit-cart-drawer__collections');
      if (collections && collections.children.length) return; // cards already present
      if (collections) {
        collections.outerHTML = cachedEmptyCollections;
      } else {
        emptyState.insertAdjacentHTML('beforeend', cachedEmptyCollections);
      }
    }

    /**
     * Open the cart drawer
     */
    function openDrawer() {
      // The cart page is already a full cart view — popping the drawer over it is
      // redundant and let the two carts drift out of sync (removing an item on the
      // page left stale items in the drawer). Suppress it there.
      if (document.body.classList.contains('template-cart')) return;

      previouslyFocusedElement = document.activeElement;
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollbarWidth + 'px';
      drawer.classList.add(CLASSES.open);
      document.body.style.overflow = 'hidden';

      // Focus the close button after transition
      setTimeout(function () {
        var closeBtn = drawer.querySelector(SELECTORS.closeBtn);
        if (closeBtn) closeBtn.focus();
      }, 310);

      // Listen for escape key
      document.addEventListener('keydown', handleKeyDown);
    }

    /**
     * Close the cart drawer
     */
    function closeDrawer() {
      drawer.classList.remove(CLASSES.open);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // Return focus to the element that opened the drawer
      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
        previouslyFocusedElement = null;
      }

      // Remove escape key listener
      document.removeEventListener('keydown', handleKeyDown);
    }

    /**
     * Handle keyboard events
     */
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      // Focus trap within the drawer panel
      if (event.key === 'Tab') {
        trapFocus(event);
      }
    }

    /**
     * Trap focus within the drawer panel
     */
    function trapFocus(event) {
      const focusableElements = panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    /**
     * Re-render the panel using Shopify's section_id approach (same pattern as base theme)
     */
    function refreshDrawer() {
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(rootUrl + 'cart?section_id=oddit-cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newPanel = doc.querySelector(SELECTORS.panel);
          if (newPanel) panel.innerHTML = newPanel.innerHTML;
          populateCartRecommendations();
          restoreEmptyStateCards();
        });
    }

    function populateCartRecommendations() {
      var fallback = panel.querySelector('[data-cart-rec-fallback]');
      if (!fallback) return;
      var grid = fallback.querySelector('[data-cart-rec-grid]');
      if (!grid || grid.children.length) return;
      var productId = fallback.getAttribute('data-cart-rec-product-id');
      if (!productId) return;

      var cartIds = (fallback.getAttribute('data-cart-product-ids') || '').split(',');
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url =
        rootUrl +
        'recommendations/products?product_id=' +
        productId +
        '&intent=related&limit=6&section_id=oddit-cart-recommendations';

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('.oddit-cart-drawer__upsell-card');
          var added = 0;
          cards.forEach(function (card) {
            if (added >= 2) return;
            var pid = card.getAttribute('data-product-id');
            if (pid && cartIds.indexOf(pid) !== -1) return; // already in cart
            grid.appendChild(card);
            added += 1;
          });
          if (added > 0) fallback.hidden = false;
        })
        .catch(function () {});
    }

    /**
     * Immediately update DOM visibility based on cart API response.
     * Acts as a guaranteed fallback if refreshDrawer() fails.
     */
    function applyCartState(cart, key, quantity) {
      if (quantity === 0) {
        var lineItem = panel.querySelector('.oddit-cart-drawer__line-item[data-line-item-key="' + key + '"]');
        if (lineItem) lineItem.parentNode.removeChild(lineItem);
      }

      var itemsState = panel.querySelector('[data-cart-items]');
      var emptyState = panel.querySelector('.oddit-cart-drawer__empty-state');
      var footer = drawer.querySelector('.oddit-cart-drawer__footer');
      var shippingBanner = drawer.querySelector('.oddit-cart-drawer__shipping-banner');

      if (cart.item_count === 0) {
        if (itemsState) itemsState.style.display = 'none';
        if (emptyState) emptyState.style.display = '';
        if (footer) footer.style.display = 'none';
        if (shippingBanner) shippingBanner.style.display = 'none';
        restoreEmptyStateCards();
      }
    }

    /**
     * Disable/enable interactive buttons during fetch
     */
    function setLoading(isLoading) {
      panel.querySelectorAll(
        '.oddit-cart-drawer__quantity-btn, .oddit-cart-drawer__remove-btn, .oddit-cart-drawer__rec-add'
      ).forEach(function (btn) {
        btn.disabled = isLoading;
      });
    }

    /**
     * POST to /cart/change.js then refresh the drawer
     */
    function cartChange(key, quantity) {
      var lineItem = panel.querySelector('.oddit-cart-drawer__line-item[data-line-item-key="' + key + '"]');
      if (lineItem) lineItem.classList.add('is-pending');
      setLoading(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          applyCartState(cart, key, quantity);
          return refreshDrawer().finally(function () {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          });
        })
        .finally(function () {
          setLoading(false);
          panel.querySelectorAll('.is-pending').forEach(function (el) {
            el.classList.remove('is-pending');
          });
        });
    }

    /**
     * POST to /cart/add.js then refresh the drawer
     */
    function cartAdd(variantId) {
      setLoading(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          return refreshDrawer().then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          });
        })
        .finally(function () {
          setLoading(false);
          panel.querySelectorAll('.is-pending').forEach(function (el) {
            el.classList.remove('is-pending');
          });
        });
    }

    /**
     * Cleanup event listeners
     */
    function destroy() {
      overlay.removeEventListener('click', closeDrawer);
      document.removeEventListener('keydown', handleKeyDown);
    }

    // Delegated click handler for close, qty, remove, and upsell actions
    drawer.addEventListener('click', function (e) {
      if (e.target.closest(SELECTORS.closeBtn)) { closeDrawer(); return; }

      var actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        var action = actionBtn.getAttribute('data-action');
        var key = actionBtn.getAttribute('data-line-item-key');
        if (action === 'remove') {
          cartChange(key, 0);
        } else if (action === 'decrease' || action === 'increase') {
          var qty = parseInt(actionBtn.getAttribute('data-quantity'), 10);
          if (!isNaN(qty) && qty >= 0) cartChange(key, qty);
        }
        return;
      }

      var upsellBtn = e.target.closest('.oddit-cart-drawer__rec-add');
      if (upsellBtn) {
        var variantId = upsellBtn.getAttribute('data-variant-id');
        if (variantId) cartAdd(variantId);
      }
    });

    overlay.addEventListener('click', closeDrawer);

    // Fill the recommendations fallback for the server-rendered cart on load.
    populateCartRecommendations();

    // Show/hide shipping banner based on cart state
    document.addEventListener('cart:updated', function (e) {
      var shippingBanner = drawer.querySelector('.oddit-cart-drawer__shipping-banner');
      if (!shippingBanner) return;
      var itemCount = e.detail && e.detail.cart && e.detail.cart.item_count;
      shippingBanner.style.display = itemCount > 0 ? '' : 'none';
    });

    // Expose open/close methods globally for external triggers (e.g. cart icon button)
    window.odditCartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      toggle: function () {
        if (drawer.classList.contains(CLASSES.open)) {
          closeDrawer();
        } else {
          openDrawer();
        }
      },
      refresh: refreshDrawer,
      destroy: destroy,
    };
  }

  // Initialize on DOM ready
  initCartDrawer();

  // Theme editor support: re-initialize when section is loaded/reloaded
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector(SELECTORS.drawer);
    if (section) {
      initCartDrawer(section);
    }
  });

  document.addEventListener('shopify:section:unload', function () {
    if (window.odditCartDrawer && typeof window.odditCartDrawer.destroy === 'function') {
      window.odditCartDrawer.destroy();
    }
  });

  /* Quick-shop toggle handled globally by oddit-collection-product-card.js */
})();